import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { ApiService } from '../api.service';
import { ApiCreateService } from '../api.create.service';
import { Project } from '../models';

@Injectable({ providedIn: 'root' })
export class ProjectDomainService {
  private readonly api = inject(ApiService);
  private readonly createApi = inject(ApiCreateService);

  private readonly projectsSignal = signal<Project[] | null>(null);
  readonly projects = computed(() => this.projectsSignal());

  private readonly currentProjectSignal = signal<Project | null>(null);
  readonly currentProject = computed(() => this.currentProjectSignal());

  private readonly unclaimedIdsCountSignal = signal<number>(0);
  readonly unclaimedIdsCount = computed(() => this.unclaimedIdsCountSignal());

  loadProjects(): Observable<Project[]> {
    return this.api.getProjects().pipe(tap((projects) => this.projectsSignal.set(projects)));
  }

  loadProject(projectId: string): Observable<Project> {
    return this.api
      .getProject(projectId)
      .pipe(tap((project) => this.currentProjectSignal.set(project)));
  }

  loadProjectUnclaimedIds(projectId: string): Observable<string[]> {
    return this.api
      .getProjectUnclaimedIds(projectId)
      .pipe(tap((ids) => this.unclaimedIdsCountSignal.set(ids.length)));
  }

  createProject(name: string): Observable<Project> {
    return this.createApi.createProject(name).pipe(
      tap((project) => {
        const current = this.projectsSignal();
        this.projectsSignal.set(current ? [...current, project] : [project]);
        this.currentProjectSignal.set(project);
      }),
    );
  }

  updateProject(project: Project): Observable<Project> {
    return this.createApi.putProject(project).pipe(
      tap((updated) => {
        const current = this.projectsSignal();
        if (current) {
          this.projectsSignal.set(current.map((p) => (p.id === updated.id ? updated : p)));
        }
        this.currentProjectSignal.set(updated);
      }),
    );
  }

  /**
   * Clear the current project selection, e.g. when navigating
   * back to the top-level project list where no single project
   * should be considered active.
   */
  clearCurrentProject(): void {
    this.currentProjectSignal.set(null);
  }

  downloadProjectUnclaimedIdsCsv(projectId: string) {
    return this.api.downloadProjectUnclaimedIdsCsv(projectId);
  }
}
